import React, { useEffect, useState } from "react";
import { datadogRum } from "@datadog/browser-rum";
import Dropdown from "../DropDown";
import { fetchWithCache } from "../../CacheDB/cached-Ref-Api";

interface ResourceType {
  resource_type_code: string;
  code_description: string;
  sys_created_by: string;
  sys_modified_by: string;
}

interface ResourceTypeDropdownProps {
  selectedType: string;
  onTypeChange: (
    selectedType: string,
    selectedTypeObject?: ResourceType
  ) => void;
  disabled: boolean;
  className?: string;
  onFocus?: () => void;
}

const ResourceTypeDropdown: React.FC<ResourceTypeDropdownProps> = ({
  selectedType,
  onTypeChange,
  disabled,
  className = "",
  onFocus,
}) => {
  const [types, setTypes] = useState<ResourceType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchResourceTypes = async () => {
      const startTime = performance.now();
      try {
        const data = await fetchWithCache("resource-type", "/ref-data/resource-type");

        if (Array.isArray(data)) {
          setTypes(data);
          datadogRum.addAction("Fetched Resource Types Successfully", {
            count: data.length,
          });
        } else {
          throw new Error("Invalid response format");
        }
      } catch (err: any) {
        setError("Failed to fetch resource types");
        console.error("Error fetching resource types:", err);
        datadogRum.addError("Failed to fetch resource types", { error: err });
      } finally {
        setLoading(false);
        const endTime = performance.now();
        datadogRum.addAction("Resource Type API Response Time", {
          duration: endTime - startTime,
        });
      }
    };

    fetchResourceTypes();
  }, []);

  const options = types.map((type) => ({
    value: type.resource_type_code,
    label: type.resource_type_code,
  }));

  const handleChange = (selectedValue: string) => {
    const selected = types.find((t) => t.resource_type_code === selectedValue);
    onTypeChange(selectedValue, selected);
  };

  return (
    <Dropdown
      options={options}
      selectedValue={selectedType}
      onValueChange={handleChange}
      disabled={disabled || loading || !!error}
      className={className}
      placeholder={loading ? "Loading Resource Types..." : error ?? "Select Resource Type"}
      onFocus={onFocus}
    />
  );
};

export default ResourceTypeDropdown;
